// pages/AdminPage.jsx — Full content management system

import { useState, useCallback, useRef } from "react";
import { Button, Input, Textarea, Label, Card, Toggle, Modal, Badge } from "../components/UI";
import { genId, fileToDataUrl } from "../utils/helpers";
import { DEFAULT_CONTENT } from "../data/defaultContent";

// ── autoTranslate — MyMemory API (رایگان، بدون API key) ──────────────────────
async function autoTranslate(texts) {
  const MYMEMORY_URL = "https://api.mymemory.translated.net/get";
  const keys = Object.keys(texts);

  const translated = await Promise.all(
    keys.map(async (k) => {
      const v = texts[k];
      if (Array.isArray(v)) {
        const items = await Promise.all(
          v.map(async (item) => {
            if (!item || !String(item).trim()) return item;
            const res = await fetch(`${MYMEMORY_URL}?q=${encodeURIComponent(item)}&langpair=en|es`);
            const data = await res.json();
            return data.responseData?.translatedText || item;
          })
        );
        return { key: k, value: items };
      } else {
        if (!v || !String(v).trim()) return { key: k, value: v };
        const res = await fetch(`${MYMEMORY_URL}?q=${encodeURIComponent(String(v))}&langpair=en|es`);
        const data = await res.json();
        return { key: k, value: data.responseData?.translatedText || v };
      }
    })
  );

  const result = {};
  translated.forEach(({ key, value }) => { result[key] = value; });
  return result;
}



// ── LoginForm ────────────────────────────────────────────────────────────────
function LoginForm({ onLogin, dark }) {
  const [creds, setCreds] = useState({ username: "", password: "" });
  const [error, setError] = useState("");

  const submit = (e) => {
    e.preventDefault();
    const ok = onLogin(creds.username, creds.password);
    if (!ok) setError("Invalid username or password.");
  };

  return (
    <div
      className={`min-h-screen flex items-center justify-center px-4 ${
        dark ? "bg-[#0F172A] text-white" : "bg-[#E2E8F0]/30 text-[#0F172A]"
      }`}
    >
      <div
        className={`w-full max-w-sm rounded-2xl border p-8 shadow-2xl ${
          dark ? "bg-white/5 border-white/10" : "bg-white border-[#E2E8F0]"
        }`}
      >
        <div className="text-center mb-8">
          <div className="text-4xl mb-3">🔐</div>
          <h1 className="text-2xl font-bold">Admin Login</h1>
          <p className={`text-sm mt-1 ${dark ? "text-white/45" : "text-[#0F172A]/45"}`}>
            WebStudio Content Manager
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label dark={dark}>Username</Label>
            <Input
              dark={dark}
              value={creds.username}
              onChange={(e) => setCreds({ ...creds, username: e.target.value })}
              placeholder="admin"
              required
              autoFocus
            />
          </div>
          <div>
            <Label dark={dark}>Password</Label>
            <Input
              dark={dark}
              type="password"
              value={creds.password}
              onChange={(e) => setCreds({ ...creds, password: e.target.value })}
              placeholder="••••••••"
              required
            />
          </div>

          {error && (
            <p className="text-red-400 text-xs">{error}</p>
          )}

          <Button type="submit" size="lg" className="w-full mt-2">
            Sign In
          </Button>
        </form>
      </div>
    </div>
  );
}

// ── ImageUploader ─────────────────────────────────────────────────────────────
// cloudinarySettings = { cloudName, uploadPreset } — اگه پاس نشه، base64 fallback
function ImageUploader({ label, value, onChange, dark, cloudinarySettings }) {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadError("");

    // اگه Cloudinary تنظیم شده، از اون استفاده کن
    if (cloudinarySettings?.cloudName && cloudinarySettings?.uploadPreset) {
      setUploading(true);
      try {
        const { uploadToCloudinary } = await import("../utils/cloudinary");
        const url = await uploadToCloudinary(
          file,
          cloudinarySettings.cloudName,
          cloudinarySettings.uploadPreset
        );
        onChange(url);
      } catch (err) {
        setUploadError("آپلود ناموفق: " + err.message);
      } finally {
        setUploading(false);
      }
    } else {
      // Fallback به base64 (localhost / بدون Cloudinary)
      if (file.size > 500 * 1024) {
        setUploadError("⚠️ فایل بزرگتر از 500KB است. برای بهترین نتیجه از Cloudinary یا URL استفاده کن.");
      }
      try {
        const { fileToDataUrl } = await import("../utils/helpers");
        const url = await fileToDataUrl(file);
        onChange(url);
      } catch {
        setUploadError("خطا در خواندن فایل");
      }
    }
    // reset input
    if (inputRef.current) inputRef.current.value = "";
  };

  const isConfigured = cloudinarySettings?.cloudName && cloudinarySettings?.uploadPreset;

  return (
    <div>
      {label && <Label dark={dark}>{label}</Label>}
      <div className="flex gap-3 items-start">
        {value && (
          <img
            src={value}
            alt="Preview"
            className="w-16 h-16 rounded-xl object-cover border border-white/10 shrink-0"
          />
        )}
        <div className="flex-1 space-y-2">
          <Input
            dark={dark}
            value={value || ""}
            onChange={(e) => { setUploadError(""); onChange(e.target.value); }}
            placeholder="https://... یا فایل آپلود کن"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={uploading}
              onClick={() => inputRef.current?.click()}
              className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                uploading
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:border-[#A3E635] hover:text-[#A3E635]"
              } ${dark ? "border-white/15 text-white/50" : "border-[#E2E8F0] text-[#0F172A]/50"}`}
            >
              {uploading ? "⏳ در حال آپلود..." : isConfigured ? "☁️ Upload to Cloudinary" : "📁 Upload file"}
            </button>
            {!isConfigured && (
              <span className={`text-xs ${dark ? "text-white/25" : "text-[#0F172A]/25"}`}>
                (برای آپلود مستقیم، Cloudinary رو در Settings تنظیم کن)
              </span>
            )}
          </div>
          {uploadError && (
            <p className="text-xs text-amber-400">{uploadError}</p>
          )}
          <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
        </div>
      </div>
    </div>
  );
}

// ── Section panel wrapper ────────────────────────────────────────────────────
function Panel({ title, children, dark }) {
  const [open, setOpen] = useState(true);
  return (
    <div className={`rounded-2xl border overflow-hidden ${dark ? "border-white/10" : "border-[#E2E8F0]"}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between px-5 py-4 font-semibold text-left transition-colors ${
          open
            ? "bg-[#A3E635] text-[#0F172A]"
            : dark ? "bg-white/5 hover:bg-white/8 text-white" : "bg-[#E2E8F0]/40 hover:bg-[#E2E8F0]/70 text-[#0F172A]"
        }`}
      >
        <span>{title}</span>
        <span className={`text-lg transition-transform ${open ? "rotate-180" : ""}`}>⌃</span>
      </button>
      {open && (
        <div className={`p-5 space-y-5 ${dark ? "bg-white/[0.02]" : "bg-white"}`}>
          {children}
        </div>
      )}
    </div>
  );
}

// ── SubServiceEditor ──────────────────────────────────────────────────────────
function SubServiceEditor({ items, onChange, dark }) {
  const update = (i, val) => {
    const next = [...items];
    next[i] = val;
    onChange(next);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, ""]);

  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex gap-2">
          <Input dark={dark} value={item} onChange={(e) => update(i, e.target.value)} placeholder="Sub-service..." />
          <Button variant="danger" size="sm" onClick={() => remove(i)}>✕</Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={add} className={dark ? "border-white/20 text-white/60" : ""}>+ Add</Button>
    </div>
  );
}

// ── BulletEditor ──────────────────────────────────────────────────────────────
function BulletEditor({ items, onChange, dark }) {
  const update = (i, val) => { const next = [...items]; next[i] = val; onChange(next); };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, ""]);

  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex gap-2">
          <Input dark={dark} value={item} onChange={(e) => update(i, e.target.value)} placeholder="Feature..." />
          <Button variant="danger" size="sm" onClick={() => remove(i)}>✕</Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={add} className={dark ? "border-white/20 text-white/60" : ""}>+ Add bullet</Button>
    </div>
  );
}

// ── MultiImageEditor ──────────────────────────────────────────────────────────
function MultiImageEditor({ images, onChange, dark }) {
  const inputRef = useRef();
  const remove = (i) => onChange(images.filter((_, idx) => idx !== i));
  const addUrl = () => onChange([...images, ""]);
  const updateUrl = (i, val) => { const next = [...images]; next[i] = val; onChange(next); };
  const handleFile = async (e) => {
    const files = Array.from(e.target.files);
    const urls = await Promise.all(files.map(fileToDataUrl));
    onChange([...images, ...urls]);
    e.target.value = "";
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {images.map((img, i) => (
          <div key={i} className="relative group">
            <img src={img} alt={`img-${i}`} className="w-20 h-16 object-cover rounded-xl border border-white/10" />
            <button
              onClick={() => remove(i)}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
      {images.map((img, i) => (
        <Input key={i} dark={dark} value={img} onChange={(e) => updateUrl(i, e.target.value)} placeholder="Image URL..." className="text-xs" />
      ))}
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={addUrl} className={dark ? "border-white/20 text-white/60" : ""}>+ URL</Button>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className={`text-xs px-3 py-1.5 rounded-lg border transition-colors hover:border-[#A3E635] hover:text-[#A3E635] ${
            dark ? "border-white/15 text-white/50" : "border-[#E2E8F0] text-[#0F172A]/50"
          }`}
        >
          📁 Upload
        </button>
        <input ref={inputRef} type="file" accept="image/*" multiple onChange={handleFile} className="hidden" />
      </div>
    </div>
  );
}

// ── Main AdminPage ────────────────────────────────────────────────────────────
// ── HeroPanel — with EN + ES fields + Auto-translate ─────────────────────────
function HeroPanel({  content, upd, cloudinarySettings, dark}) {
  const [translating, setTranslating] = useState(false);

  const translate = async () => {
    setTranslating(true);
    try {
      const result = await autoTranslate({
        titleEs: content.hero.title,
        subtitleEs: content.hero.subtitle,
        ctaTextEs: content.hero.ctaText,
        viewWorkTextEs: content.hero.viewWorkText || "View Our Work ↓",
      });
      upd("hero", { ...content.hero, ...result });
    } catch (e) { alert("Translation failed. Try again."); }
    setTranslating(false);
  };

  return (
    <>
      <Panel title="Hero — English" dark={dark}>
        <div>
          <Label dark={dark}>Headline (EN)</Label>
          <Input dark={dark} value={content.hero.title} onChange={(e) => upd("hero", { ...content.hero, title: e.target.value })} />
        </div>
        <div>
          <Label dark={dark}>Subtitle (EN)</Label>
          <Textarea dark={dark} rows={3} value={content.hero.subtitle} onChange={(e) => upd("hero", { ...content.hero, subtitle: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label dark={dark}>CTA Button (EN)</Label>
            <Input dark={dark} value={content.hero.ctaText} onChange={(e) => upd("hero", { ...content.hero, ctaText: e.target.value })} />
          </div>
          <div>
            <Label dark={dark}>CTA Link</Label>
            <Input dark={dark} value={content.hero.ctaLink} onChange={(e) => upd("hero", { ...content.hero, ctaLink: e.target.value })} placeholder="#contact" />
          </div>
        </div>
        <ImageUploader cloudinarySettings={cloudinarySettings} label="Background Image" value={content.hero.bgImage} onChange={(url) => upd("hero", { ...content.hero, bgImage: url })} dark={dark} />
      </Panel>

      <Panel title="Hero — Español" dark={dark}>
        <button onClick={translate} disabled={translating} className="w-full mb-4 py-2.5 px-4 rounded-xl bg-[#A3E635]/10 border border-[#A3E635]/30 text-[#A3E635] text-sm font-semibold hover:bg-[#A3E635]/20 transition-colors disabled:opacity-50">
          {translating ? "⏳ Traduciendo..." : "🤖 Auto-translate EN → ES"}
        </button>
        <div>
          <Label dark={dark}>Título (ES)</Label>
          <Input dark={dark} value={content.hero.titleEs || ""} onChange={(e) => upd("hero", { ...content.hero, titleEs: e.target.value })} placeholder="Sitios Web Hermosos que Convierten" />
        </div>
        <div>
          <Label dark={dark}>Subtítulo (ES)</Label>
          <Textarea dark={dark} rows={3} value={content.hero.subtitleEs || ""} onChange={(e) => upd("hero", { ...content.hero, subtitleEs: e.target.value })} />
        </div>
        <div>
          <Label dark={dark}>Botón CTA (ES)</Label>
          <Input dark={dark} value={content.hero.ctaTextEs || ""} onChange={(e) => upd("hero", { ...content.hero, ctaTextEs: e.target.value })} placeholder="Inicia Tu Proyecto" />
        </div>
      </Panel>
    </>
  );
}

// ── CTAPanel — EN + ES + Auto-translate ──────────────────────────────────────
function CTAPanel({  content, upd, dark}) {
  const [translating, setTranslating] = useState(false);

  const translate = async () => {
    setTranslating(true);
    try {
      const result = await autoTranslate({
        titleEs: content.cta.title,
        subtitleEs: content.cta.subtitle,
        buttonTextEs: content.cta.buttonText,
      });
      upd("cta", { ...content.cta, ...result });
    } catch { alert("Translation failed. Try again."); }
    setTranslating(false);
  };

  return (
    <>
      <Panel title="CTA — English" dark={dark}>
        <div><Label dark={dark}>Title (EN)</Label><Input dark={dark} value={content.cta.title} onChange={(e) => upd("cta", { ...content.cta, title: e.target.value })} /></div>
        <div><Label dark={dark}>Subtitle (EN)</Label><Textarea dark={dark} rows={2} value={content.cta.subtitle} onChange={(e) => upd("cta", { ...content.cta, subtitle: e.target.value })} /></div>
        <div><Label dark={dark}>Button Text (EN)</Label><Input dark={dark} value={content.cta.buttonText} onChange={(e) => upd("cta", { ...content.cta, buttonText: e.target.value })} /></div>
        <div><Label dark={dark}>Link</Label><Input dark={dark} value={content.cta.link} onChange={(e) => upd("cta", { ...content.cta, link: e.target.value })} placeholder="#contact" /></div>
      </Panel>
      <Panel title="CTA — Español" dark={dark}>
        <button onClick={translate} disabled={translating} className="w-full mb-4 py-2.5 px-4 rounded-xl bg-[#A3E635]/10 border border-[#A3E635]/30 text-[#A3E635] text-sm font-semibold hover:bg-[#A3E635]/20 transition-colors disabled:opacity-50">
          {translating ? "⏳ Traduciendo..." : "🤖 Auto-translate EN → ES"}
        </button>
        <div><Label dark={dark}>Título (ES)</Label><Input dark={dark} value={content.cta.titleEs || ""} onChange={(e) => upd("cta", { ...content.cta, titleEs: e.target.value })} /></div>
        <div><Label dark={dark}>Subtítulo (ES)</Label><Textarea dark={dark} rows={2} value={content.cta.subtitleEs || ""} onChange={(e) => upd("cta", { ...content.cta, subtitleEs: e.target.value })} /></div>
        <div><Label dark={dark}>Botón (ES)</Label><Input dark={dark} value={content.cta.buttonTextEs || ""} onChange={(e) => upd("cta", { ...content.cta, buttonTextEs: e.target.value })} /></div>
      </Panel>
    </>
  );
}

// ── PricingTranslateButton ────────────────────────────────────────────────────
function PricingTranslateButton({  tier, idx, content, upd, dark}) {
  const [translating, setTranslating] = useState(false);
  const translate = async () => {
    setTranslating(true);
    try {
      const result = await autoTranslate({ nameEs: tier.name, bulletsEs: tier.bullets });
      const next = [...content.pricing.tiers];
      next[idx] = { ...tier, ...result };
      upd("pricing", { ...content.pricing, tiers: next });
    } catch { alert("Translation failed. Try again."); }
    setTranslating(false);
  };
  return (
    <button onClick={translate} disabled={translating} className="w-full py-2 px-4 rounded-xl bg-[#A3E635]/10 border border-[#A3E635]/30 text-[#A3E635] text-xs font-semibold hover:bg-[#A3E635]/20 transition-colors disabled:opacity-50">
      {translating ? "⏳ Traduciendo..." : "🤖 Auto-translate EN → ES"}
    </button>
  );
}

// ── PortfolioTranslateButton ──────────────────────────────────────────────────
function PortfolioTranslateButton({  proj, idx, content, upd, dark}) {
  const [translating, setTranslating] = useState(false);
  const translate = async () => {
    setTranslating(true);
    try {
      const result = await autoTranslate({ titleEs: proj.title, descriptionEs: proj.description, categoryEs: proj.category });
      const next = [...content.portfolio.projects];
      next[idx] = { ...proj, ...result };
      upd("portfolio", { ...content.portfolio, projects: next });
    } catch { alert("Translation failed. Try again."); }
    setTranslating(false);
  };
  return (
    <button onClick={translate} disabled={translating} className="w-full py-2 px-4 rounded-xl bg-[#A3E635]/10 border border-[#A3E635]/30 text-[#A3E635] text-xs font-semibold hover:bg-[#A3E635]/20 transition-colors disabled:opacity-50">
      {translating ? "⏳ Traduciendo..." : "🤖 Auto-translate EN → ES"}
    </button>
  );
}

// ── SplitSectionPanel ─────────────────────────────────────────────────────────
function SplitSectionPanel({  content, upd, cloudinarySettings, dark}) {
  const [translating, setTranslating] = useState(false);
  const ss = content.splitSection || {};

  const translate = async () => {
    setTranslating(true);
    try {
      const result = await autoTranslate({
        badgeEs: ss.badge,
        headingEs: ss.heading,
        paragraph1Es: ss.paragraph1,
        paragraph2Es: ss.paragraph2,
        paragraph3Es: ss.paragraph3,
      });
      upd("splitSection", { ...ss, ...result });
    } catch { alert("Translation failed. Try again."); }
    setTranslating(false);
  };

  return (
    <>
      <Panel title="Split Section — English" dark={dark}>
        <div><Label dark={dark}>Badge (EN)</Label><Input dark={dark} value={ss.badge || ""} onChange={(e) => upd("splitSection", { ...ss, badge: e.target.value })} placeholder="Design & Development" /></div>
        <div><Label dark={dark}>Heading (EN)</Label><Input dark={dark} value={ss.heading || ""} onChange={(e) => upd("splitSection", { ...ss, heading: e.target.value })} /></div>
        <div><Label dark={dark}>Paragraph 1 (EN)</Label><Textarea dark={dark} rows={3} value={ss.paragraph1 || ""} onChange={(e) => upd("splitSection", { ...ss, paragraph1: e.target.value })} /></div>
        <div><Label dark={dark}>Paragraph 2 (EN)</Label><Textarea dark={dark} rows={3} value={ss.paragraph2 || ""} onChange={(e) => upd("splitSection", { ...ss, paragraph2: e.target.value })} /></div>
        <div><Label dark={dark}>Paragraph 3 (EN)</Label><Textarea dark={dark} rows={3} value={ss.paragraph3 || ""} onChange={(e) => upd("splitSection", { ...ss, paragraph3: e.target.value })} /></div>
        <ImageUploader label="Image" dark={dark} value={ss.image || ""} onChange={(url) => upd("splitSection", { ...ss, image: url })} cloudinarySettings={cloudinarySettings} />
        <div><Label dark={dark}>Image Alt Text</Label><Input dark={dark} value={ss.imageAlt || ""} onChange={(e) => upd("splitSection", { ...ss, imageAlt: e.target.value })} /></div>
      </Panel>

      <Panel title="Split Section — Español" dark={dark}>
        <button onClick={translate} disabled={translating} className="w-full mb-4 py-2.5 px-4 rounded-xl bg-[#A3E635]/10 border border-[#A3E635]/30 text-[#A3E635] text-sm font-semibold hover:bg-[#A3E635]/20 transition-colors disabled:opacity-50">
          {translating ? "⏳ Traduciendo..." : "🤖 Auto-translate EN → ES"}
        </button>
        <div><Label dark={dark}>Badge (ES)</Label><Input dark={dark} value={ss.badgeEs || ""} onChange={(e) => upd("splitSection", { ...ss, badgeEs: e.target.value })} /></div>
        <div><Label dark={dark}>Título (ES)</Label><Input dark={dark} value={ss.headingEs || ""} onChange={(e) => upd("splitSection", { ...ss, headingEs: e.target.value })} /></div>
        <div><Label dark={dark}>Párrafo 1 (ES)</Label><Textarea dark={dark} rows={3} value={ss.paragraph1Es || ""} onChange={(e) => upd("splitSection", { ...ss, paragraph1Es: e.target.value })} /></div>
        <div><Label dark={dark}>Párrafo 2 (ES)</Label><Textarea dark={dark} rows={3} value={ss.paragraph2Es || ""} onChange={(e) => upd("splitSection", { ...ss, paragraph2Es: e.target.value })} /></div>
        <div><Label dark={dark}>Párrafo 3 (ES)</Label><Textarea dark={dark} rows={3} value={ss.paragraph3Es || ""} onChange={(e) => upd("splitSection", { ...ss, paragraph3Es: e.target.value })} /></div>
      </Panel>
    </>
  );
}

// ── TestimonialTranslateButton ────────────────────────────────────────────────
function TestimonialTranslateButton({  item, idx, content, upd, dark}) {
  const [translating, setTranslating] = useState(false);
  const translate = async () => {
    setTranslating(true);
    try {
      const result = await autoTranslate({ textEs: item.text, roleEs: item.role });
      const next = [...content.testimonials];
      next[idx] = { ...item, ...result };
      upd("testimonials", next);
    } catch { alert("Translation failed. Try again."); }
    setTranslating(false);
  };
  return (
    <button onClick={translate} disabled={translating} className="w-full py-2 px-4 rounded-xl bg-[#A3E635]/10 border border-[#A3E635]/30 text-[#A3E635] text-xs font-semibold hover:bg-[#A3E635]/20 transition-colors disabled:opacity-50">
      {translating ? "⏳ Traduciendo..." : "🤖 Auto-translate EN → ES"}
    </button>
  );
}

// ── ServiceTranslateButton — per-service auto-translate ───────────────────────
function ServiceTranslateButton({  svc, idx, content, upd, dark}) {
  const [translating, setTranslating] = useState(false);
  const translate = async () => {
    setTranslating(true);
    try {
      const result = await autoTranslate({
        titleEs: svc.title,
        descriptionEs: svc.description,
        subServicesEs: svc.subServices,
      });
      const next = [...content.services];
      next[idx] = { ...svc, ...result };
      upd("services", next);
    } catch { alert("Translation failed. Try again."); }
    setTranslating(false);
  };
  return (
    <button onClick={translate} disabled={translating} className="w-full py-2 px-4 rounded-xl bg-[#A3E635]/10 border border-[#A3E635]/30 text-[#A3E635] text-xs font-semibold hover:bg-[#A3E635]/20 transition-colors disabled:opacity-50">
      {translating ? "⏳ Traduciendo..." : "🤖 Auto-translate EN → ES"}
    </button>
  );
}

// ── ContactPanel — EN + ES + Auto-translate ───────────────────────────────────
function ContactPanel({  content, upd, dark}) {
  const [translating, setTranslating] = useState(false);

  const translate = async () => {
    setTranslating(true);
    try {
      const result = await autoTranslate({
        titleEs: content.contact.title,
        subtitleEs: content.contact.subtitle,
        namePlaceholderEs: content.contact.namePlaceholder,
        emailPlaceholderEs: content.contact.emailPlaceholder,
        messagePlaceholderEs: content.contact.messagePlaceholder,
        sendButtonTextEs: content.contact.sendButtonText,
      });
      upd("contact", { ...content.contact, ...result });
    } catch { alert("Translation failed. Try again."); }
    setTranslating(false);
  };

  return (
    <>
      <Panel title="Contact — English" dark={dark}>
        <div className="grid grid-cols-2 gap-3">
          <div><Label dark={dark}>Section Title (EN)</Label><Input dark={dark} value={content.contact.title} onChange={(e) => upd("contact", { ...content.contact, title: e.target.value })} /></div>
          <div><Label dark={dark}>Subtitle (EN)</Label><Input dark={dark} value={content.contact.subtitle} onChange={(e) => upd("contact", { ...content.contact, subtitle: e.target.value })} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label dark={dark}>Email</Label><Input dark={dark} type="email" value={content.contact.email} onChange={(e) => upd("contact", { ...content.contact, email: e.target.value })} /></div>
          <div><Label dark={dark}>WhatsApp Number</Label><Input dark={dark} value={content.contact.whatsapp} onChange={(e) => upd("contact", { ...content.contact, whatsapp: e.target.value })} placeholder="+34 600 000 000" /></div>
        </div>
        <div><Label dark={dark}>Name placeholder (EN)</Label><Input dark={dark} value={content.contact.namePlaceholder} onChange={(e) => upd("contact", { ...content.contact, namePlaceholder: e.target.value })} /></div>
        <div><Label dark={dark}>Email placeholder (EN)</Label><Input dark={dark} value={content.contact.emailPlaceholder} onChange={(e) => upd("contact", { ...content.contact, emailPlaceholder: e.target.value })} /></div>
        <div><Label dark={dark}>Message placeholder (EN)</Label><Textarea dark={dark} rows={2} value={content.contact.messagePlaceholder} onChange={(e) => upd("contact", { ...content.contact, messagePlaceholder: e.target.value })} /></div>
        <div><Label dark={dark}>Send button (EN)</Label><Input dark={dark} value={content.contact.sendButtonText} onChange={(e) => upd("contact", { ...content.contact, sendButtonText: e.target.value })} /></div>
      </Panel>

      <Panel title="Contact — Español" dark={dark}>
        <button onClick={translate} disabled={translating} className="w-full mb-4 py-2.5 px-4 rounded-xl bg-[#A3E635]/10 border border-[#A3E635]/30 text-[#A3E635] text-sm font-semibold hover:bg-[#A3E635]/20 transition-colors disabled:opacity-50">
          {translating ? "⏳ Traduciendo..." : "🤖 Auto-translate EN → ES"}
        </button>
        <div><Label dark={dark}>Título (ES)</Label><Input dark={dark} value={content.contact.titleEs || ""} onChange={(e) => upd("contact", { ...content.contact, titleEs: e.target.value })} /></div>
        <div><Label dark={dark}>Subtítulo (ES)</Label><Input dark={dark} value={content.contact.subtitleEs || ""} onChange={(e) => upd("contact", { ...content.contact, subtitleEs: e.target.value })} /></div>
        <div><Label dark={dark}>Placeholder nombre (ES)</Label><Input dark={dark} value={content.contact.namePlaceholderEs || ""} onChange={(e) => upd("contact", { ...content.contact, namePlaceholderEs: e.target.value })} /></div>
        <div><Label dark={dark}>Placeholder email (ES)</Label><Input dark={dark} value={content.contact.emailPlaceholderEs || ""} onChange={(e) => upd("contact", { ...content.contact, emailPlaceholderEs: e.target.value })} /></div>
        <div><Label dark={dark}>Placeholder mensaje (ES)</Label><Textarea dark={dark} rows={2} value={content.contact.messagePlaceholderEs || ""} onChange={(e) => upd("contact", { ...content.contact, messagePlaceholderEs: e.target.value })} /></div>
        <div><Label dark={dark}>Botón enviar (ES)</Label><Input dark={dark} value={content.contact.sendButtonTextEs || ""} onChange={(e) => upd("contact", { ...content.contact, sendButtonTextEs: e.target.value })} /></div>
      </Panel>
    </>
  );
}

export default function AdminPage({ content, updateContent, publishDraft, discardDraft, resetContent, hasUnsaved, isAdmin, login, logout, dark, setDark }) {
  const [activeSection, setActiveSection] = useState("logo");
  const [publishedToast, setPublishedToast] = useState(false);


  if (!isAdmin) {
    return <LoginForm onLogin={login} dark={dark} />;
  }

  // Cloudinary settings shortcut
  const cloudinarySettings = content.cloudinary || { cloudName: "", uploadPreset: "" };

  // Utility updater for nested sections
  const upd = (section, value) =>
    updateContent((prev) => ({ ...prev, [section]: value }));

  const handlePublish = async () => {
    const success = await publishDraft();
    if (success !== false) {
      setPublishedToast(true);
      setTimeout(() => setPublishedToast(false), 3000);
    }
  };

  const handleDiscard = () => {
    if (window.confirm("Discard all unsaved changes and revert to last published version?")) {
      discardDraft();
    }
  };

  const handleLogout = () => {
    if (hasUnsaved) {
      if (!window.confirm("You have unsaved changes! If you logout now your changes will be lost.\n\nLogout anyway?")) {
        return;
      }
    }
    logout();
  };

  const handleReset = () => {
    if (window.confirm("Reset ALL content to defaults? This cannot be undone.")) {
      resetContent();
    }
  };

  const sections = [
    { key: "logo", label: "🖼 Logo" },
    { key: "settings", label: "⚙️ Settings" },
    { key: "hero", label: "🦸 Hero" },
    { key: "services", label: "🔧 Services" },
    { key: "splitSection", label: "🖼 Split Section" },
    { key: "portfolio", label: "💼 Portfolio" },
    { key: "pricing", label: "💰 Pricing" },
    { key: "testimonials", label: "⭐ Testimonials" },
    { key: "cta", label: "📣 CTA" },
    { key: "contact", label: "📬 Contact" },
    { key: "footer", label: "🦶 Footer" },
    { key: "seo", label: "🔍 SEO" },
    { key: "whatsapp", label: "💬 WhatsApp" },
    { key: "techStack", label: "⚡ Tech Stack" },
    { key: "sectionBg", label: "🎨 Section Backgrounds" },
  ];

  return (
    <div className={`min-h-screen ${dark ? "bg-[#0F172A] text-white" : "bg-[#E2E8F0]/20 text-[#0F172A]"}`}>
      {/* Top bar */}
      <div className={`sticky top-0 z-40 border-b px-4 py-3 flex items-center justify-between ${dark ? "bg-[#0F172A]/95 border-white/10 backdrop-blur-xl" : "bg-white/95 border-[#E2E8F0] backdrop-blur-xl shadow-sm"}`}>
        <div className="flex items-center gap-3">
          <a href="/" className="text-lg font-bold">
            <span className="text-[#A3E635]">Web</span>Studio
          </a>
          <span className={`text-xs px-2 py-0.5 rounded-full ${dark ? "bg-white/10 text-white/50" : "bg-[#E2E8F0] text-[#0F172A]/50"}`}>Admin</span>
          {/* Unsaved indicator */}
          {hasUnsaved && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-400 border border-amber-400/20">
              ● Unsaved changes
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Published toast */}
          {publishedToast && (
            <span className="text-xs text-[#A3E635] font-semibold px-3 py-1.5 bg-[#A3E635]/10 rounded-lg">
              ✓ Published!
            </span>
          )}

          {/* Discard button — فقط وقتی unsaved داریم */}
          {hasUnsaved && (
            <Button variant="ghost" size="sm" onClick={handleDiscard} className="text-amber-400 hover:text-amber-300">
              ✕ Discard
            </Button>
          )}

          {/* Preview */}
          <Button variant="outline" size="sm" onClick={() => window.open('/?preview=draft', '_blank')} className={dark ? "border-white/20 text-white/60" : ""}>
            👁 Preview
          </Button>

          {/* Save & Publish */}
          <Button
            size="sm"
            onClick={handlePublish}
            disabled={!hasUnsaved}
            className={!hasUnsaved ? "opacity-40 cursor-not-allowed" : "shadow-lg shadow-[#A3E635]/20"}
          >
            🚀 Save & Publish
          </Button>

          <Button variant="ghost" size="sm" onClick={() => setDark(!dark)} className="text-lg">
            {dark ? "☀️" : "🌙"}
          </Button>
          <Button variant="ghost" size="sm" onClick={handleLogout} className={dark ? "text-white/50" : "text-[#0F172A]/50"}>
            Logout
          </Button>
        </div>
      </div>

      <div className="flex min-h-[calc(100vh-57px)]">
        {/* Sidebar */}
        <aside className={`w-56 shrink-0 border-r p-4 space-y-1 ${dark ? "border-white/10 bg-white/[0.02]" : "border-[#E2E8F0] bg-white"}`}>
          {sections.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveSection(key)}
              className={`w-full text-left text-sm px-3 py-2.5 rounded-xl transition-all ${
                activeSection === key
                  ? "bg-[#A3E635] text-[#0F172A] font-semibold"
                  : dark
                  ? "text-white/60 hover:bg-white/5 hover:text-white"
                  : "text-[#0F172A]/60 hover:bg-[#E2E8F0]/50 hover:text-[#0F172A]"
              }`}
            >
              {label}
            </button>
          ))}
          <div className="pt-4 border-t mt-4" style={{ borderColor: dark ? "rgba(255,255,255,0.1)" : "#E2E8F0" }}>
            <button
              onClick={handleReset}
              className="w-full text-left text-xs px-3 py-2 rounded-xl text-red-400 hover:bg-red-500/10 transition-colors"
            >
              🔄 Reset to defaults
            </button>
          </div>
        </aside>

        {/* Main editor area */}
        <main className="flex-1 p-6 overflow-y-auto">
          <div className="max-w-2xl space-y-6">

            {/* ── SETTINGS ── */}
            {activeSection === "settings" && (
              <>
              <Panel title="⚙️ Cloudinary — تنظیمات آپلود عکس" dark={dark}>
                <div className={`rounded-xl p-4 text-sm space-y-1 ${dark ? "bg-blue-500/10 border border-blue-500/20 text-blue-300" : "bg-blue-50 border border-blue-200 text-blue-700"}`}>
                  <p className="font-semibold">راهنمای تنظیم Cloudinary:</p>
                  <p>۱. به <a href="https://cloudinary.com" target="_blank" rel="noreferrer" className="underline">cloudinary.com</a> برو و ثبت‌نام کن (رایگان)</p>
                  <p>۲. از داشبورد <strong>Cloud Name</strong> رو کپی کن</p>
                  <p>۳. برو Settings → Upload → Add upload preset</p>
                  <p>۴. Mode رو <strong>Unsigned</strong> بذار و ذخیره کن</p>
                  <p>۵. اسم preset رو اینجا وارد کن</p>
                </div>
                <div>
                  <Label dark={dark}>Cloud Name</Label>
                  <Input
                    dark={dark}
                    value={cloudinarySettings.cloudName}
                    onChange={(e) => upd("cloudinary", { ...cloudinarySettings, cloudName: e.target.value.trim() })}
                    placeholder="مثال: my-agency-name"
                  />
                </div>
                <div>
                  <Label dark={dark}>Upload Preset (Unsigned)</Label>
                  <Input
                    dark={dark}
                    value={cloudinarySettings.uploadPreset}
                    onChange={(e) => upd("cloudinary", { ...cloudinarySettings, uploadPreset: e.target.value.trim() })}
                    placeholder="مثال: webstudio_unsigned"
                  />
                </div>
                {cloudinarySettings.cloudName && cloudinarySettings.uploadPreset ? (
                  <div className={`rounded-xl p-3 text-xs font-semibold ${dark ? "bg-[#A3E635]/10 text-[#A3E635]" : "bg-[#A3E635]/10 text-[#0F172A]"}`}>
                    ✅ Cloudinary تنظیم شده — آپلودها به Cloudinary میرن
                  </div>
                ) : (
                  <div className={`rounded-xl p-3 text-xs ${dark ? "bg-white/5 text-white/40" : "bg-[#E2E8F0]/50 text-[#0F172A]/40"}`}>
                    ⚠️ هنوز تنظیم نشده — آپلودها به صورت base64 ذخیره میشن (فقط برای localhost مناسبه)
                  </div>
                )}
              </Panel>
              </>
            )}

            {/* ── LOGO ── */}
            {activeSection === "logo" && (
              <Panel title="Logo Settings" dark={dark}>
                <div>
                  <Label dark={dark}>Logo Text</Label>
                  <Input dark={dark} value={content.logo.text} onChange={(e) => upd("logo", { ...content.logo, text: e.target.value })} placeholder="WebStudio" />
                </div>
                <ImageUploader cloudinarySettings={cloudinarySettings} label="☀️ Logo — Light Mode" value={content.logo.imageUrl || ""} onChange={(url) => upd("logo", { ...content.logo, imageUrl: url })} dark={dark} />
                {content.logo.imageUrl && (
                  <Button variant="danger" size="sm" onClick={() => upd("logo", { ...content.logo, imageUrl: null })}>Remove Light Logo</Button>
                )}
                <ImageUploader cloudinarySettings={cloudinarySettings} label="🌙 Logo — Dark Mode" value={content.logo.imageDarkUrl || ""} onChange={(url) => upd("logo", { ...content.logo, imageDarkUrl: url })} dark={dark} />
                {content.logo.imageDarkUrl && (
                  <Button variant="danger" size="sm" onClick={() => upd("logo", { ...content.logo, imageDarkUrl: null })}>Remove Dark Logo</Button>
                )}
              </Panel>
            )}

            {/* ── HERO ── */}
            {activeSection === "hero" && (
              <HeroPanel content={content} upd={upd} cloudinarySettings={cloudinarySettings} dark={dark} />
            )}


            {/* ── SERVICES ── */}
            {activeSection === "services" && (
              <>
                <Panel title="📱 Mobile Layout" dark={dark}>
                  <Toggle
                    checked={content.servicesMobileSwiper ?? true}
                    onChange={(val) => upd("servicesMobileSwiper", val)}
                    label={content.servicesMobileSwiper ?? true ? "Mobile: Horizontal Swiper (swipe) ↔️" : "Mobile: Vertical List (scroll) ↕️"}
                  />
                  <p className={`text-xs mt-1 ${dark ? "text-white/35" : "text-[#0F172A]/35"}`}>
                    فقط روی موبایل تأثیر داره — دسکتاپ همیشه grid سه‌ستونه
                  </p>
                </Panel>

                {content.services.map((svc, idx) => (
                  <Panel key={svc.id} title={`Service ${idx + 1}: ${svc.title}`} dark={dark}>
                    {/* EN fields */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label dark={dark}>Icon (emoji)</Label>
                        <Input dark={dark} value={svc.icon} onChange={(e) => { const next = [...content.services]; next[idx] = { ...svc, icon: e.target.value }; upd("services", next); }} placeholder="🚀" />
                      </div>
                      <div>
                        <Label dark={dark}>Title (EN)</Label>
                        <Input dark={dark} value={svc.title} onChange={(e) => { const next = [...content.services]; next[idx] = { ...svc, title: e.target.value }; upd("services", next); }} />
                      </div>
                    </div>
                    <div>
                      <Label dark={dark}>Description (EN)</Label>
                      <Textarea dark={dark} rows={2} value={svc.description} onChange={(e) => { const next = [...content.services]; next[idx] = { ...svc, description: e.target.value }; upd("services", next); }} />
                    </div>
                    <ImageUploader cloudinarySettings={cloudinarySettings} label="Icon Image (optional)" value={svc.imageUrl} onChange={(url) => { const next = [...content.services]; next[idx] = { ...svc, imageUrl: url }; upd("services", next); }} dark={dark} />
                    <div>
                      <Label dark={dark}>Sub-services (EN)</Label>
                      <SubServiceEditor items={svc.subServices || []} onChange={(items) => { const next = [...content.services]; next[idx] = { ...svc, subServices: items }; upd("services", next); }} dark={dark} />
                    </div>

                    {/* ES fields */}
                    <div className={`mt-4 pt-4 border-t space-y-3 ${dark ? "border-white/10" : "border-[#E2E8F0]"}`}>
                      <ServiceTranslateButton svc={svc} idx={idx} content={content} upd={upd} dark={dark} />
                      <div>
                        <Label dark={dark}>Título (ES)</Label>
                        <Input dark={dark} value={svc.titleEs || ""} onChange={(e) => { const next = [...content.services]; next[idx] = { ...svc, titleEs: e.target.value }; upd("services", next); }} />
                      </div>
                      <div>
                        <Label dark={dark}>Descripción (ES)</Label>
                        <Textarea dark={dark} rows={2} value={svc.descriptionEs || ""} onChange={(e) => { const next = [...content.services]; next[idx] = { ...svc, descriptionEs: e.target.value }; upd("services", next); }} />
                      </div>
                      <div>
                        <Label dark={dark}>Sub-servicios (ES)</Label>
                        <SubServiceEditor items={svc.subServicesEs || []} onChange={(items) => { const next = [...content.services]; next[idx] = { ...svc, subServicesEs: items }; upd("services", next); }} dark={dark} />
                      </div>
                    </div>

                    <Button variant="danger" size="sm" onClick={() => { if (window.confirm("Remove this service?")) { upd("services", content.services.filter((_, i) => i !== idx)); } }}>🗑 Remove Service</Button>
                  </Panel>
                ))}
                <Button variant="outline" onClick={() => upd("services", [...content.services, { id: genId(), icon: "🔧", title: "New Service", description: "", subServices: [] }])} className={dark ? "border-white/20 text-white/60 w-full" : "w-full"}>
                  + Add Service
                </Button>
              </>
            )}


            {/* ── PORTFOLIO ── */}
            {activeSection === "portfolio" && (
              <>
                <Panel title="Portfolio Settings" dark={dark}>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label dark={dark}>Cards visible (full)</Label>
                      <Input dark={dark} type="number" min={1} max={10} value={content.portfolio.numberOfCardsToShow} onChange={(e) => upd("portfolio", { ...content.portfolio, numberOfCardsToShow: parseInt(e.target.value) || 1 })} />
                    </div>
                    <div>
                      <Label dark={dark}>Partial card hint %</Label>
                      <Input dark={dark} type="number" min={0} max={90} value={content.portfolio.partialCardVisiblePercent} onChange={(e) => upd("portfolio", { ...content.portfolio, partialCardVisiblePercent: parseInt(e.target.value) || 0 })} />
                    </div>
                  </div>
                </Panel>

                {content.portfolio.projects.map((proj, idx) => (
                  <Panel key={proj.id} title={`Project ${idx + 1}: ${proj.title}`} dark={dark}>
                    {/* EN fields */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label dark={dark}>Title (EN)</Label>
                        <Input dark={dark} value={proj.title} onChange={(e) => { const next = [...content.portfolio.projects]; next[idx] = { ...proj, title: e.target.value }; upd("portfolio", { ...content.portfolio, projects: next }); }} />
                      </div>
                      <div>
                        <Label dark={dark}>Category (EN)</Label>
                        <Input dark={dark} value={proj.category} onChange={(e) => { const next = [...content.portfolio.projects]; next[idx] = { ...proj, category: e.target.value }; upd("portfolio", { ...content.portfolio, projects: next }); }} />
                      </div>
                    </div>
                    <div>
                      <Label dark={dark}>Description (EN)</Label>
                      <Textarea dark={dark} rows={2} value={proj.description} onChange={(e) => { const next = [...content.portfolio.projects]; next[idx] = { ...proj, description: e.target.value }; upd("portfolio", { ...content.portfolio, projects: next }); }} />
                    </div>

                    <ImageUploader label="🖼 Card Thumbnail" value={proj.thumbnail || ""} cloudinarySettings={cloudinarySettings} onChange={(url) => { const next = [...content.portfolio.projects]; next[idx] = { ...proj, thumbnail: url }; upd("portfolio", { ...content.portfolio, projects: next }); }} dark={dark} />

                    <div>
                      <Label dark={dark}>Images (screens)</Label>
                      <div className="space-y-4">
                        {(proj.images || []).map((img, imgIdx) => (
                          <div key={imgIdx} className={`rounded-xl border p-4 space-y-3 ${dark ? "border-white/10 bg-white/[0.02]" : "border-[#E2E8F0] bg-[#E2E8F0]/20"}`}>
                            <div className="flex items-center justify-between">
                              <span className={`text-xs font-semibold ${dark ? "text-white/40" : "text-[#0F172A]/40"}`}>Screen {imgIdx + 1}</span>
                              <Button variant="danger" size="sm" onClick={() => { const nextImgs = (proj.images || []).filter((_, i) => i !== imgIdx); const next = [...content.portfolio.projects]; next[idx] = { ...proj, images: nextImgs }; upd("portfolio", { ...content.portfolio, projects: next }); }}>✕</Button>
                            </div>
                            <ImageUploader label="🖥 Desktop" value={img.desktop || ""} cloudinarySettings={cloudinarySettings} onChange={(url) => { const nextImgs = [...(proj.images || [])]; nextImgs[imgIdx] = { ...img, desktop: url }; const next = [...content.portfolio.projects]; next[idx] = { ...proj, images: nextImgs }; upd("portfolio", { ...content.portfolio, projects: next }); }} dark={dark} />
                            <ImageUploader label="📱 Mobile" value={img.mobile || ""} cloudinarySettings={cloudinarySettings} onChange={(url) => { const nextImgs = [...(proj.images || [])]; nextImgs[imgIdx] = { ...img, mobile: url }; const next = [...content.portfolio.projects]; next[idx] = { ...proj, images: nextImgs }; upd("portfolio", { ...content.portfolio, projects: next }); }} dark={dark} />
                            <div>
                              <Label dark={dark}>Alt Text (SEO)</Label>
                              <Input dark={dark} value={img.alt || ""} onChange={(e) => { const nextImgs = [...(proj.images || [])]; nextImgs[imgIdx] = { ...img, alt: e.target.value }; const next = [...content.portfolio.projects]; next[idx] = { ...proj, images: nextImgs }; upd("portfolio", { ...content.portfolio, projects: next }); }} placeholder="Image description for SEO..." />
                            </div>
                          </div>
                        ))}
                        <Button variant="outline" size="sm" onClick={() => { const nextImgs = [...(proj.images || []), { desktop: "", mobile: "", alt: "" }]; const next = [...content.portfolio.projects]; next[idx] = { ...proj, images: nextImgs }; upd("portfolio", { ...content.portfolio, projects: next }); }} className={dark ? "border-white/20 text-white/60 w-full" : "w-full"}>
                          + Add Screen
                        </Button>
                      </div>
                    </div>

                    {/* ES fields */}
                    <div className={`mt-4 pt-4 border-t space-y-3 ${dark ? "border-white/10" : "border-[#E2E8F0]"}`}>
                      <PortfolioTranslateButton proj={proj} idx={idx} content={content} upd={upd} dark={dark} />
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label dark={dark}>Título (ES)</Label><Input dark={dark} value={proj.titleEs || ""} onChange={(e) => { const next = [...content.portfolio.projects]; next[idx] = { ...proj, titleEs: e.target.value }; upd("portfolio", { ...content.portfolio, projects: next }); }} /></div>
                        <div><Label dark={dark}>Categoría (ES)</Label><Input dark={dark} value={proj.categoryEs || ""} onChange={(e) => { const next = [...content.portfolio.projects]; next[idx] = { ...proj, categoryEs: e.target.value }; upd("portfolio", { ...content.portfolio, projects: next }); }} /></div>
                      </div>
                      <div><Label dark={dark}>Descripción (ES)</Label><Textarea dark={dark} rows={2} value={proj.descriptionEs || ""} onChange={(e) => { const next = [...content.portfolio.projects]; next[idx] = { ...proj, descriptionEs: e.target.value }; upd("portfolio", { ...content.portfolio, projects: next }); }} /></div>
                    </div>

                    <Button variant="danger" size="sm" onClick={() => { if (window.confirm("Remove this project?")) { const next = content.portfolio.projects.filter((_, i) => i !== idx); upd("portfolio", { ...content.portfolio, projects: next }); } }}>🗑 Remove Project</Button>
                  </Panel>
                ))}

                <Button variant="outline" onClick={() => upd("portfolio", { ...content.portfolio, projects: [...content.portfolio.projects, { id: genId(), title: "New Project", category: "Other", description: "", images: [{ desktop: "", mobile: "", alt: "" }] }] })} className={dark ? "border-white/20 text-white/60 w-full" : "w-full"}>
                  + Add Project
                </Button>
              </>
            )}


            {/* ── PRICING ── */}
            {activeSection === "pricing" && (
              <>
                <Panel title="Pricing Settings" dark={dark}>
                  <div>
                    <Label dark={dark}>Number of tiers to show</Label>
                    <Input dark={dark} type="number" min={1} max={content.pricing.tiers.length} value={content.pricing.numberOfTiersToShow} onChange={(e) => upd("pricing", { ...content.pricing, numberOfTiersToShow: parseInt(e.target.value) || 1 })} />
                  </div>
                </Panel>

                {content.pricing.tiers.map((tier, idx) => (
                  <Panel key={tier.id} title={`Tier ${idx + 1}: ${tier.name}`} dark={dark}>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label dark={dark}>Tier Name (EN)</Label>
                        <Input dark={dark} value={tier.name} onChange={(e) => { const next = [...content.pricing.tiers]; next[idx] = { ...tier, name: e.target.value }; upd("pricing", { ...content.pricing, tiers: next }); }} />
                      </div>
                      <div>
                        <Label dark={dark}>Price</Label>
                        <Input dark={dark} value={tier.price} onChange={(e) => { const next = [...content.pricing.tiers]; next[idx] = { ...tier, price: e.target.value }; upd("pricing", { ...content.pricing, tiers: next }); }} placeholder="€999" />
                      </div>
                    </div>
                    <div>
                      <Label dark={dark}>Original Price — leave empty to hide</Label>
                      <Input dark={dark} value={tier.originalPrice || ""} onChange={(e) => { const next = [...content.pricing.tiers]; next[idx] = { ...tier, originalPrice: e.target.value }; upd("pricing", { ...content.pricing, tiers: next }); }} placeholder="€1,299 (optional)" />
                    </div>
                    <Toggle checked={tier.recommended} onChange={(val) => { const next = content.pricing.tiers.map((t, i) => i === idx ? { ...t, recommended: val } : { ...t, recommended: false }); upd("pricing", { ...content.pricing, tiers: next }); }} label="Mark as Recommended" />
                    <div>
                      <Label dark={dark}>Bullet Points (EN)</Label>
                      <BulletEditor items={tier.bullets || []} onChange={(bullets) => { const next = [...content.pricing.tiers]; next[idx] = { ...tier, bullets }; upd("pricing", { ...content.pricing, tiers: next }); }} dark={dark} />
                    </div>

                    {/* ES */}
                    <div className={`mt-4 pt-4 border-t space-y-3 ${dark ? "border-white/10" : "border-[#E2E8F0]"}`}>
                      <PricingTranslateButton tier={tier} idx={idx} content={content} upd={upd} dark={dark} />
                      <div>
                        <Label dark={dark}>Nombre del Plan (ES)</Label>
                        <Input dark={dark} value={tier.nameEs || ""} onChange={(e) => { const next = [...content.pricing.tiers]; next[idx] = { ...tier, nameEs: e.target.value }; upd("pricing", { ...content.pricing, tiers: next }); }} />
                      </div>
                      <div>
                        <Label dark={dark}>Características (ES)</Label>
                        <BulletEditor items={tier.bulletsEs || []} onChange={(bullets) => { const next = [...content.pricing.tiers]; next[idx] = { ...tier, bulletsEs: bullets }; upd("pricing", { ...content.pricing, tiers: next }); }} dark={dark} />
                      </div>
                    </div>

                    <Button variant="danger" size="sm" onClick={() => { if (window.confirm("Remove this pricing tier?")) { const next = content.pricing.tiers.filter((_, i) => i !== idx); upd("pricing", { ...content.pricing, tiers: next, numberOfTiersToShow: Math.min(content.pricing.numberOfTiersToShow, next.length) }); } }}>🗑 Remove Tier</Button>
                  </Panel>
                ))}
                <Button variant="outline" onClick={() => upd("pricing", { ...content.pricing, tiers: [...content.pricing.tiers, { id: genId(), name: "New Tier", price: "€0", recommended: false, bullets: [] }], numberOfTiersToShow: content.pricing.numberOfTiersToShow + 1 })} className={dark ? "border-white/20 text-white/60 w-full" : "w-full"}>
                  + Add Pricing Tier
                </Button>
              </>
            )}

            {/* ── TESTIMONIALS ── */}
            {activeSection === "testimonials" && (
              <>
                {content.testimonials.map((item, idx) => (
                  <Panel key={item.id} title={`Testimonial ${idx + 1}: ${item.author}`} dark={dark}>
                    <div>
                      <Label dark={dark}>Quote Text (EN)</Label>
                      <Textarea dark={dark} rows={3} value={item.text} onChange={(e) => { const next = [...content.testimonials]; next[idx] = { ...item, text: e.target.value }; upd("testimonials", next); }} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label dark={dark}>Author Name</Label><Input dark={dark} value={item.author} onChange={(e) => { const next = [...content.testimonials]; next[idx] = { ...item, author: e.target.value }; upd("testimonials", next); }} /></div>
                      <div><Label dark={dark}>Role (EN)</Label><Input dark={dark} value={item.role || ""} onChange={(e) => { const next = [...content.testimonials]; next[idx] = { ...item, role: e.target.value }; upd("testimonials", next); }} /></div>
                    </div>
                    <ImageUploader cloudinarySettings={cloudinarySettings} label="Avatar Image" value={item.avatar} onChange={(url) => { const next = [...content.testimonials]; next[idx] = { ...item, avatar: url }; upd("testimonials", next); }} dark={dark} />

                    {/* ES */}
                    <div className={`mt-4 pt-4 border-t space-y-3 ${dark ? "border-white/10" : "border-[#E2E8F0]"}`}>
                      <TestimonialTranslateButton item={item} idx={idx} content={content} upd={upd} dark={dark} />
                      <div><Label dark={dark}>Texto del testimonio (ES)</Label><Textarea dark={dark} rows={3} value={item.textEs || ""} onChange={(e) => { const next = [...content.testimonials]; next[idx] = { ...item, textEs: e.target.value }; upd("testimonials", next); }} /></div>
                      <div><Label dark={dark}>Cargo / Empresa (ES)</Label><Input dark={dark} value={item.roleEs || ""} onChange={(e) => { const next = [...content.testimonials]; next[idx] = { ...item, roleEs: e.target.value }; upd("testimonials", next); }} /></div>
                    </div>

                    <Button variant="danger" size="sm" onClick={() => { if (window.confirm("Remove testimonial?")) { upd("testimonials", content.testimonials.filter((_, i) => i !== idx)); } }}>🗑 Remove</Button>
                  </Panel>
                ))}
                <Button variant="outline" onClick={() => upd("testimonials", [...content.testimonials, { id: genId(), text: "Great experience!", author: "New Client", role: "Company", avatar: null }])} className={dark ? "border-white/20 text-white/60 w-full" : "w-full"}>
                  + Add Testimonial
                </Button>
              </>
            )}

            {activeSection === "cta" && (
              <CTAPanel content={content} upd={upd} dark={dark} />
            )}


            {/* ── CONTACT ── */}
            {activeSection === "contact" && (
              <ContactPanel content={content} upd={upd} dark={dark} />
            )}

            {/* ── SPLIT SECTION ── */}
            {activeSection === "splitSection" && (
              <SplitSectionPanel content={content} upd={upd} cloudinarySettings={cloudinarySettings} dark={dark} />
            )}

            {activeSection === "techStack" && (
              <>
                <Panel title="Tech Stack Settings" dark={dark}>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label dark={dark}>Show Tech Stack Section</Label>
                      <Toggle
                        checked={content.techStack?.enabled ?? true}
                        onChange={(val) => upd("techStack", { ...content.techStack, enabled: val })}
                        dark={dark}
                      />
                    </div>
                    <div>
                      <Label dark={dark}>Section Title (EN)</Label>
                      <Input dark={dark} value={content.techStack?.sectionTitle || ""} onChange={(e) => upd("techStack", { ...content.techStack, sectionTitle: e.target.value })} placeholder="Tech Stack" />
                    </div>
                    <div>
                      <Label dark={dark}>Section Subtitle (EN)</Label>
                      <Input dark={dark} value={content.techStack?.sectionSubtitle || ""} onChange={(e) => upd("techStack", { ...content.techStack, sectionSubtitle: e.target.value })} placeholder="Technologies I use..." />
                    </div>
                    <div className={`pt-3 border-t ${dark ? "border-white/10" : "border-[#E2E8F0]"}`}>
                      <Label dark={dark}>Título de sección (ES)</Label>
                      <Input dark={dark} value={content.techStack?.sectionTitleEs || ""} onChange={(e) => upd("techStack", { ...content.techStack, sectionTitleEs: e.target.value })} placeholder="Tecnologías" />
                    </div>
                    <div>
                      <Label dark={dark}>Subtítulo de sección (ES)</Label>
                      <Input dark={dark} value={content.techStack?.sectionSubtitleEs || ""} onChange={(e) => upd("techStack", { ...content.techStack, sectionSubtitleEs: e.target.value })} placeholder="Las tecnologías que utilizo..." />
                    </div>
                  </div>
                </Panel>

                <Panel title={`Technologies (${content.techStack?.items?.length || 0})`} dark={dark}>
                  <div className="space-y-3">
                    {(content.techStack?.items || []).map((item, idx) => (
                      <div key={item.id} className={`rounded-xl border p-4 space-y-3 ${dark ? "border-white/10 bg-white/3" : "border-[#E2E8F0] bg-[#F8FAFC]"}`}>
                        <div className="flex items-center justify-between">
                          <span className={`text-sm font-semibold ${dark ? "text-white" : "text-[#0F172A]"}`}>{item.name || "New Item"}</span>
                          <button
                            onClick={() => {
                              const next = content.techStack.items.filter((_, i) => i !== idx);
                              upd("techStack", { ...content.techStack, items: next });
                            }}
                            className="text-xs text-red-400 hover:text-red-300 px-2 py-1 rounded-lg border border-red-400/30 hover:border-red-300/50 transition-colors"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label dark={dark}>Name</Label>
                            <Input dark={dark} value={item.name} onChange={(e) => {
                              const next = [...content.techStack.items];
                              next[idx] = { ...next[idx], name: e.target.value };
                              upd("techStack", { ...content.techStack, items: next });
                            }} placeholder="React" />
                          </div>
                          <div>
                            <Label dark={dark}>Category</Label>
                            <select
                              value={item.category}
                              onChange={(e) => {
                                const next = [...content.techStack.items];
                                next[idx] = { ...next[idx], category: e.target.value };
                                upd("techStack", { ...content.techStack, items: next });
                              }}
                              className={`w-full rounded-xl px-3 py-2 text-sm border outline-none transition-colors ${dark ? "bg-white/5 border-white/15 text-white" : "bg-white border-[#E2E8F0] text-[#0F172A]"}`}
                            >
                              <option value="Frontend">Frontend</option>
                              <option value="Design">Design</option>
                              <option value="Tools">Tools</option>
                            </select>
                          </div>
                        </div>
                        <div>
                          <Label dark={dark}>Logo URL</Label>
                          <Input dark={dark} value={item.logo || ""} onChange={(e) => {
                            const next = [...content.techStack.items];
                            next[idx] = { ...next[idx], logo: e.target.value };
                            upd("techStack", { ...content.techStack, items: next });
                          }} placeholder="https://cdn.../icon.svg" />
                        </div>
                      </div>
                    ))}

                    <button
                      onClick={() => {
                        const newItem = { id: `ts${Date.now()}`, name: "", category: "Frontend", logo: "" };
                        upd("techStack", { ...content.techStack, items: [...(content.techStack?.items || []), newItem] });
                      }}
                      className="w-full py-2.5 rounded-xl border-2 border-dashed text-sm font-medium transition-colors border-[#A3E635]/40 text-[#A3E635] hover:border-[#A3E635] hover:bg-[#A3E635]/5"
                    >
                      + Add Technology
                    </button>
                  </div>
                </Panel>
              </>
            )}

            {/* ── WHATSAPP ── */}
            {/* ── SEO ── */}
            {activeSection === "seo" && (
              <>
                <Panel title="SEO — English" dark={dark}>
                  <div className="space-y-4">
                    <div>
                      <Label dark={dark}>Page Title (EN)</Label>
                      <Input dark={dark} value={content.seo?.titleEn || ""} onChange={(e) => upd("seo", { ...content.seo, titleEn: e.target.value })} placeholder="Webilo — Web Design in Spain" />
                    </div>
                    <div>
                      <Label dark={dark}>Meta Description (EN)</Label>
                      <Textarea dark={dark} rows={3} value={content.seo?.descriptionEn || ""} onChange={(e) => upd("seo", { ...content.seo, descriptionEn: e.target.value })} />
                    </div>
                    <div>
                      <Label dark={dark}>Keywords (EN) — comma separated</Label>
                      <Textarea dark={dark} rows={2} value={content.seo?.keywordsEn || ""} onChange={(e) => upd("seo", { ...content.seo, keywordsEn: e.target.value })} />
                    </div>
                  </div>
                </Panel>

                <Panel title="SEO — Español" dark={dark}>
                  <div className="space-y-4">
                    <div>
                      <Label dark={dark}>Título de Página (ES)</Label>
                      <Input dark={dark} value={content.seo?.titleEs || ""} onChange={(e) => upd("seo", { ...content.seo, titleEs: e.target.value })} placeholder="Webilo — Diseño Web en España" />
                    </div>
                    <div>
                      <Label dark={dark}>Meta Descripción (ES)</Label>
                      <Textarea dark={dark} rows={3} value={content.seo?.descriptionEs || ""} onChange={(e) => upd("seo", { ...content.seo, descriptionEs: e.target.value })} />
                    </div>
                    <div>
                      <Label dark={dark}>Palabras Clave (ES) — separadas por coma</Label>
                      <Textarea dark={dark} rows={2} value={content.seo?.keywordsEs || ""} onChange={(e) => upd("seo", { ...content.seo, keywordsEs: e.target.value })} />
                    </div>
                  </div>
                </Panel>

                <Panel title="Social Share Image (og:image)" dark={dark}>
                  <div className="space-y-4">
                    <p className={`text-xs ${dark ? "text-white/40" : "text-[#0F172A]/40"}`}>
                      این تصویر موقع share کردن لینک سایت در واتس‌آپ، لینکدین، توییتر و گوگل نمایش داده میشه. سایز پیشنهادی: 1200×630 پیکسل.
                    </p>
                    <ImageUploader
                      label="og:image URL"
                      dark={dark}
                      value={content.seo?.ogImage || ""}
                      onChange={(url) => upd("seo", { ...content.seo, ogImage: url })}
                      cloudinarySettings={cloudinarySettings}
                    />
                    {content.seo?.ogImage && (
                      <img src={content.seo.ogImage} alt="og:image preview" className="w-full rounded-xl object-cover h-36 border border-white/10" />
                    )}
                  </div>
                </Panel>
              </>
            )}

            {activeSection === "whatsapp" && (
              <>
                <Panel title="WhatsApp Button" dark={dark}>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label dark={dark}>Show WhatsApp Button</Label>
                      <Toggle
                        checked={content.whatsapp?.enabled ?? false}
                        onChange={(val) => upd("whatsapp", { ...content.whatsapp, enabled: val })}
                        dark={dark}
                      />
                    </div>
                    <div>
                      <Label dark={dark}>Phone Number (with country code)</Label>
                      <Input dark={dark} value={content.whatsapp?.phone || ""} onChange={(e) => upd("whatsapp", { ...content.whatsapp, phone: e.target.value })} placeholder="+34612345678" />
                    </div>
                    <div>
                      <Label dark={dark}>Pre-filled Message</Label>
                      <Textarea dark={dark} rows={3} value={content.whatsapp?.message || ""} onChange={(e) => upd("whatsapp", { ...content.whatsapp, message: e.target.value })} placeholder="Hello! I'm interested in your services." />
                    </div>
                    <div>
                      <Label dark={dark}>Tooltip Label</Label>
                      <Input dark={dark} value={content.whatsapp?.label || ""} onChange={(e) => upd("whatsapp", { ...content.whatsapp, label: e.target.value })} placeholder="Chat on WhatsApp" />
                    </div>
                  </div>
                </Panel>
              </>
            )}

            {activeSection === "sectionBg" && (
              <Panel title="Section Background Tints" dark={dark}>
                <p className={`text-xs mb-4 ${dark ? "text-white/40" : "text-[#0F172A]/40"}`}>
                  روشن کردن هر گزینه یه رنگ پس‌زمینه ملایم به اون سکشن اضافه می‌کنه. به صورت پیش‌فرض خاموش است.
                </p>
                <div className="space-y-4">
                  <Toggle
                    checked={content.sectionBg?.portfolio ?? false}
                    onChange={(val) => upd("sectionBg", { ...content.sectionBg, portfolio: val })}
                    label="Portfolio — Section Background"
                  />
                  <Toggle
                    checked={content.sectionBg?.pricing ?? false}
                    onChange={(val) => upd("sectionBg", { ...content.sectionBg, pricing: val })}
                    label="Pricing — Section Background"
                  />
                  <Toggle
                    checked={content.sectionBg?.contact ?? false}
                    onChange={(val) => upd("sectionBg", { ...content.sectionBg, contact: val })}
                    label="Contact — Section Background"
                  />
                </div>
              </Panel>
            )}

            {/* ── FOOTER ── */}
            {activeSection === "footer" && (
              <Panel title="Footer" dark={dark}>
                <div>
                  <Label dark={dark}>Brand Name</Label>
                  <Input dark={dark} value={content.footer.brand} onChange={(e) => upd("footer", { ...content.footer, brand: e.target.value })} />
                </div>
                <div>
                  <Label dark={dark}>Tagline</Label>
                  <Input dark={dark} value={content.footer.tagline || ""} onChange={(e) => upd("footer", { ...content.footer, tagline: e.target.value })} />
                </div>
                <div>
                  <Label dark={dark}>Copyright text</Label>
                  <Input dark={dark} value={content.footer.copyright || ""} onChange={(e) => upd("footer", { ...content.footer, copyright: e.target.value })} placeholder={`© ${new Date().getFullYear()} WebStudio. All rights reserved.`} />
                </div>
              </Panel>
            )}

            {/* Info box */}
            <div className={`rounded-xl border p-4 text-sm space-y-1 ${dark ? "border-white/10 bg-white/[0.02] text-white/50" : "border-[#E2E8F0] bg-[#E2E8F0]/20 text-[#0F172A]/50"}`}>
              <p>✏️ تغییرات روی <strong>Draft</strong> ذخیره میشن و روی سایت اصلی نمایش داده نمیشن.</p>
              <p>🚀 برای اعمال روی سایت، دکمه <strong>Save & Publish</strong> رو بزن.</p>
              <p>👁 با <strong>Preview</strong> می‌تونی Draft رو قبل از انتشار ببینی.</p>
            </div>
          </div>
        </main>
      </div>

    </div>
  );
}
